import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTeachersForCourse, removeTeacherFromCourse } from '../../api/teachers';
import { getCourse } from '../../api/courses';
import { extractErrorMessage } from '../../utils/api';
import { useI18n } from '../../i18n/I18nContext';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import MetricCard from '../../components/MetricCard/MetricCard';
import { formatDate } from '../../utils/datetime';
import './Teachers.css';

const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const StudentsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5-10-5Z" /><path d="M6 12v5c0 1.66 3 3 6 3s6-1.34 6-3v-5" />
  </svg>
);
const MessageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

function TeacherActivity() {
  const { t } = useI18n();
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [removeModal, setRemoveModal] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [courseData, teachersData] = await Promise.all([
          getCourse(courseId),
          getTeachersForCourse(courseId),
        ]);

        if (!cancelled) {
          setCourse(courseData);
          setTeachers(teachersData || []);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(extractErrorMessage(err, t('teacherAdmin.activity.loadFailed')));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, retryTick]);

  const handleRetryLoad = useCallback(() => setRetryTick((n) => n + 1), []);

  const handleRemoveClick = (teacher) => {
    setError('');
    setRemoveModal(teacher);
  };

  const handleCancelRemove = () => {
    setError('');
    setRemoveModal(null);
  };

  const handleConfirmRemove = async () => {
    if (!removeModal) return;

    setRemoving(true);
    setError('');
    setSuccessMessage('');

    try {
      await removeTeacherFromCourse(courseId, removeModal.id);
      setSuccessMessage(t('teacherAdmin.activity.removeSuccess', { name: removeModal.name }));
      setTeachers(teachers.filter((tc) => tc.id !== removeModal.id));
      setRemoveModal(null);
    } catch (err) {
      setError(extractErrorMessage(err, t('teacherAdmin.activity.removeFailed')));
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <div className="teachers-page">
        <h1>{t('teacherAdmin.activity.pageTitle')}</h1>
        <LoadingState skeleton="page" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="teachers-page">
        <div className="teachers-header">
          <h1>{t('teacherAdmin.activity.pageTitle')}</h1>
          <div className="teachers-header-actions">
            <button
              type="button"
              onClick={() => navigate('/courses')}
              className="teachers-back-btn"
            >
              {t('teacherAdmin.activity.backToCourses')}
            </button>
          </div>
        </div>
        <PageEmptyState
          variant="error"
          title={t('teacherAdmin.activity.loadFailed')}
          description={loadError !== t('teacherAdmin.activity.loadFailed') ? loadError : undefined}
          actionLabel={t('teacherAdmin.activity.retry')}
          onAction={handleRetryLoad}
        />
      </div>
    );
  }

  const totalStudentsAssigned = teachers.reduce((sum, tch) => sum + (tch.studentsAssigned || 0), 0);
  const totalMessages = teachers.reduce((sum, tch) => sum + (tch.messagesSentLast7Days || 0), 0);

  return (
    <div className="teachers-page">
      <div className="teachers-header">
        <h1>{t('teacherAdmin.activity.pageTitleWithCourse', { course: course?.title })}</h1>
        <div className="teachers-header-actions">
          <button
            type="button"
            onClick={() => navigate(`/courses/${courseId}/invite-teacher`)}
            className="teachers-primary-btn"
          >
            {t('teacherAdmin.activity.inviteTeacher')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/courses')}
            className="teachers-back-btn"
          >
            {t('teacherAdmin.activity.backToCourses')}
          </button>
        </div>
      </div>

      <div className="teachers-content">
        {successMessage && <div className="teachers-success">{successMessage}</div>}

        <div className="teachers-stats">
          <MetricCard
            title={t('teacherAdmin.activity.totalTeachers')}
            value={teachers.length}
            icon={<UsersIcon />}
          />
          <MetricCard
            title={t('teacherAdmin.activity.studentsAssigned')}
            value={totalStudentsAssigned}
            icon={<StudentsIcon />}
            variant="info"
          />
          <MetricCard
            title={t('teacherAdmin.activity.messagesLast7Days')}
            value={totalMessages}
            icon={<MessageIcon />}
            variant="success"
          />
        </div>

        <div className="teachers-table-wrap">
          {teachers.length === 0 ? (
            <PageEmptyState
              variant="students"
              title={t('teacherAdmin.activity.noTeachersTitle')}
              description={t('teacherAdmin.activity.noTeachersBody')}
              actionLabel={t('teacherAdmin.activity.inviteFirstTeacher')}
              onAction={() => navigate(`/courses/${courseId}/invite-teacher`)}
            />
          ) : (
            <table className="teachers-table">
              <thead>
                <tr>
                  <th>{t('teacherAdmin.activity.nameHeader')}</th>
                  <th>{t('teacherAdmin.activity.contactHeader')}</th>
                  <th>{t('teacherAdmin.activity.studentsAssignedHeader')}</th>
                  <th>{t('teacherAdmin.activity.messages7dHeader')}</th>
                  <th>{t('teacherAdmin.activity.lastActiveHeader')}</th>
                  <th>{t('teacherAdmin.activity.actionsHeader')}</th>
                </tr>
              </thead>
              <tbody>
                {teachers.map((teacher) => (
                  <tr key={teacher.id}>
                    <td>
                      <div className="teacher-info">
                        {teacher.avatar ? (
                          <img
                            src={teacher.avatar}
                            alt={teacher.name}
                            className="teacher-avatar"
                          />
                        ) : (
                          <span className="teacher-avatar-fallback" aria-hidden="true">
                            {(teacher.name || '?').trim().charAt(0).toUpperCase()}
                          </span>
                        )}
                        <span>{teacher.name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="teachers-contact-info">
                        {teacher.email && <div>{teacher.email}</div>}
                        {teacher.phone && <div>{teacher.phone}</div>}
                      </div>
                    </td>
                    <td className="numeric">{teacher.studentsAssigned || 0}</td>
                    <td className="numeric">{teacher.messagesSentLast7Days || 0}</td>
                    <td>
                      {teacher.lastActiveAt
                        ? formatDate(teacher.lastActiveAt)
                        : '-'}
                    </td>
                    <td>
                      <div className="teachers-action-buttons">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/courses/${courseId}/teachers/${teacher.id}/assign`
                            )
                          }
                          className="ds-btn ds-btn-secondary ds-btn-sm"
                        >
                          {t('teacherAdmin.activity.assignStudents')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveClick(teacher)}
                          className="ds-btn ds-btn-danger ds-btn-sm"
                        >
                          {t('teacherAdmin.activity.remove')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {removeModal && (
        <div className="teachers-modal-overlay modal-overlay" onClick={handleCancelRemove}>
          <div className="teachers-modal-content modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('teacherAdmin.activity.removeModalTitle')}</h3>
            <p>
              {t('teacherAdmin.activity.removeModalBodyPrefix')}
              <strong>{removeModal.name}</strong>
              {t('teacherAdmin.activity.removeModalBodySuffix')}
            </p>
            {error && <div className="teachers-error teachers-modal-error">{error}</div>}
            <div className="teachers-modal-actions">
              <button
                type="button"
                onClick={handleCancelRemove}
                className="ds-btn ds-btn-secondary"
                disabled={removing}
              >
                {t('teacherAdmin.activity.cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirmRemove}
                className="ds-btn ds-btn-danger"
                disabled={removing}
              >
                {removing ? t('teacherAdmin.activity.removing') : t('teacherAdmin.activity.confirmRemove')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherActivity;
